import React, { useState } from 'react';

import { BadRequestError } from '_lib/api/apiErrors';
import { Location, LocationCreateDTO } from '_lib/api/apiTypes.shim';
import { ServiceTitanLocationResource } from '_lib/api/resources/serviceTitanLocationResource';
import { BackdropSpinner } from '_lib/components/BackdropSpinner/BackdropSpinner';
import { DialogActions } from '_lib/components/DialogActions/DialogActions';
import { DialogButton } from '_lib/components/DialogButton/DialogButton';
import { DialogContent } from '_lib/components/DialogContent/DialogContent';
import { ErrorDisplay } from '_lib/components/ErrorDisplay/ErrorDisplay';
import { DialogWrapper } from '_lib/featureComponents/DialogWrapper/DialogWrapper';
import { LocationForm } from '_lib/featureComponents/Location/LocationForm';
import { AsyncDialog, AsyncDialogProps, useAsyncDialog } from '_lib/hooks/useAsyncDialog';
import { ApiSubmitHandler } from '_lib/types/hookForm.types';
import { reportException } from '_lib/utils/sentryService';

interface LocationFormDialogProps extends AsyncDialogProps<Location> {
  location: Location;
  locationResource: ServiceTitanLocationResource;
}

const LocationFormDialog = (props: LocationFormDialogProps) => {
  const { location, locationResource, onClose, titleText, ...dialogProps } = props;

  const [nonFieldErrors, setNonFieldErrors] = useState<string[] | undefined>();
  const [isSaving, setIsSaving] = useState(false);

  const handleSubmit: ApiSubmitHandler<LocationCreateDTO> = async (formLocation, { setFormErrors }) => {
    setIsSaving(true);
    setNonFieldErrors(undefined);

    try {
      // TODO - pick out location patch fields until we can refactor LocationForm to be a generic with support for
      //        our create and update use-cases
      const { contacts, ...locationPatchDTO } = formLocation;
      const updatedLocation = await locationResource.patch(location.providerId, locationPatchDTO);

      onClose({ action: 'CONFIRM', value: updatedLocation });
    } catch (err) {
      if (err instanceof BadRequestError) {
        setNonFieldErrors(err.responseData?.nonFieldErrors);
        setFormErrors(err);
      }
      reportException(err);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <DialogWrapper onClose={onClose} titleText={titleText || 'Complete Missing Location Info'} {...dialogProps}>
      <DialogContent dividers>
        <BackdropSpinner open={isSaving} />
        {nonFieldErrors?.length && <ErrorDisplay errors={nonFieldErrors} sx={{ marginTop: 1, borderRadius: 4 }} />}
        <LocationForm
          providerCustomerId={location.providerCustomerId}
          onLocationSubmit={handleSubmit}
          location={location}
          formId='st-api-call-pop-location-form-dialog'
          isDisabled={false}
        />
      </DialogContent>
      <DialogActions>
        <DialogButton type='submit' form='st-api-call-pop-location-form-dialog' text='Save' />
      </DialogActions>
    </DialogWrapper>
  );
};

interface LocationFormDialog extends AsyncDialog<Location, LocationFormDialogProps> {}
export const useLocationFormDialog = (): LocationFormDialog => useAsyncDialog(LocationFormDialog);
